import { useState } from 'react'
import { z } from 'zod'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { config } from '../../lib/config'
import { useAuthStore } from '../../stores/authStore'

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
})

export const DEBUG_ADMIN_CREDENTIALS = config.debug
  ? {
      email: 'panachai.ny+admin@gmail.com',
      password: 'prb0NDB.jda_vhr6kzt',
    }
  : null

function DebugLoginForm(): React.ReactElement | null {
  const signInWithPassword = useAuthStore((s) => s.signInWithPassword)
  const isLoading = useAuthStore((s) => s.isLoading)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isAdminSubmitting, setIsAdminSubmitting] = useState(false)

  if (!config.debug || !DEBUG_ADMIN_CREDENTIALS) return null

  async function handleAdminQuickLogin(): Promise<void> {
    if (!DEBUG_ADMIN_CREDENTIALS) return
    setFormError(null)
    setIsAdminSubmitting(true)
    setEmail(DEBUG_ADMIN_CREDENTIALS.email)
    setPassword(DEBUG_ADMIN_CREDENTIALS.password)

    const { error } = await signInWithPassword(
      DEBUG_ADMIN_CREDENTIALS.email,
      DEBUG_ADMIN_CREDENTIALS.password,
    )
    setIsAdminSubmitting(false)

    if (error) {
      setFormError(error)
    }
  }

  async function handleSubmit(
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> {
    e.preventDefault()
    setFormError(null)

    const result = loginSchema.safeParse({ email, password })
    if (!result.success) {
      setFormError(result.error.errors[0].message)
      return
    }

    setIsSubmitting(true)
    const { error } = await signInWithPassword(email, password)
    setIsSubmitting(false)

    if (error) {
      setFormError(error)
    }
  }

  const isBusy = isSubmitting || isAdminSubmitting || isLoading

  return (
    <>
      <div className="relative mb-4">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-white px-2 text-gray-400">or debug</span>
        </div>
      </div>

      <div className="mb-4">
        <Button
          type="button"
          variant="outline"
          className="flex h-auto w-full flex-col items-center justify-center border-indigo-200 bg-indigo-50/60 py-2 text-indigo-800 hover:bg-indigo-100 active:bg-indigo-200"
          onClick={handleAdminQuickLogin}
          disabled={isBusy}
        >
          <span className="font-semibold text-sm">
            {isAdminSubmitting
              ? 'Signing in as Admin…'
              : '⚡ Login as Admin (Debug)'}
          </span>
          <span className="font-normal text-indigo-600 text-xs">
            {DEBUG_ADMIN_CREDENTIALS.email}
          </span>
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="mb-4 space-y-4">
        <div className="space-y-1">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
          />
        </div>
        {formError && <p className="text-destructive text-sm">{formError}</p>}
        <Button type="submit" className="w-full" disabled={isBusy}>
          {isSubmitting ? 'Signing in…' : 'Sign In'}
        </Button>
      </form>
    </>
  )
}

export function LoginPage(): React.ReactElement {
  const signIn = useAuthStore((s) => s.signIn)
  const isLoading = useAuthStore((s) => s.isLoading)

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-100">
      <Card className="w-full max-w-md p-8">
        <h1 className="mb-2 text-center font-bold text-2xl text-gray-800">
          Login
        </h1>
        <p className="mb-8 text-center text-gray-500 text-sm">
          Sign in to access your account.
        </p>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => signIn('/auth/callback')}
          disabled={isLoading}
        >
          <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          Continue with Google
        </Button>

        <DebugLoginForm />
      </Card>
    </div>
  )
}
